
import React, { useContext } from "react";
import moment from "moment";
import { LanguageContext } from "../../context/LanguageContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const InfoCard = ({ label, value, capitalize }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <span style={{ fontSize: "12px", color: "var(--text-color-light, #94a3b8)", textTransform: "capitalize" }}>
      {label}
    </span>
    <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--cell-color, #f8fafc)", textTransform: capitalize ? "capitalize" : "none" }}>
      {value}
    </span>
  </div>
);

export const AgentDecisionDetailsModal = ({ open, data, onClose, t }) => {
  const { lang } = useContext(LanguageContext);
  const isRtl = ["ar", "ur"].includes(lang);

  if (!open || !data || !t) return null;

  const hasHumidityReadings = Array.isArray(data.extra?.humidity_readings) && data.extra.humidity_readings.length > 0;
  const chartData = hasHumidityReadings
    ? data.extra.humidity_readings.map((reading) => ({
        time: moment(reading[0]).format("HH:mm"),
        value: parseFloat(reading[1].toFixed(2)),
      }))
    : [];

  const extraDetails = { ...data.extra };
  delete extraDetails.humidity_readings;

  return (
    <div
      id="modal"
      onClick={onClose}
      style={{
        direction: isRtl ? "rtl" : "ltr",
        zIndex: 9999,
      }}
    >
      <div
        id="popup"
        className="dashboard-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "90%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 0,
          animationName: isRtl ? "reverse-add-zoom" : "add-zoom",
          backgroundColor: "var(--cell-background, #1e293b)",
          color: "var(--cell-color, #f8fafc)",
          border: "1px solid var(--modal-border, #334155)",
          borderRadius: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid var(--modal-border, #334155)",
            position: "sticky",
            top: 0,
            backgroundColor: "var(--cell-background, #1e293b)",
            zIndex: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                backgroundColor: "rgba(56, 189, 248, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#38bdf8",
              }}
            >
              <InfoOutlinedIcon />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, textTransform: "capitalize" }}>
                {data.decision_type?.replace(/_/g, " ")} {t.details}
              </h2>
              <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-color-light, #94a3b8)" }}>
                {data.room}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-color-light, #94a3b8)",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <CloseIcon />
          </button>
        </div>

        <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>
          <div
            style={{
              padding: "16px",
              backgroundColor: "rgba(56, 189, 248, 0.05)",
              border: "1px solid rgba(56, 189, 248, 0.2)",
              borderRadius: "8px",
              color: "var(--cell-color)",
              fontSize: "14px",
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: "#38bdf8", display: "block", marginBottom: "4px" }}>{t.reason}:</strong>
            {data.reason}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
            }}
          >
            <InfoCard label="Level" value={data.level?.replace(/_/g, " ")} capitalize />
            <InfoCard label="Status" value={data.execution_status?.replace(/_/g, " ")} capitalize />
            <InfoCard label="Device ID" value={data.device_id} />
            <InfoCard label={t.actor} value={data.actor?.replace(/_/g, " ")} capitalize />
            <InfoCard label={t.candidateValue} value={data.candidate_value ?? t.na} />
            <InfoCard label={t.approvedValue} value={data.approved_value ?? t.na} />
            <InfoCard label={t.loggedAt} value={moment(data.logged_at).format("DD/MM/YYYY HH:mm:ss")} />
            <InfoCard label={t.executedAt} value={moment(data.executed_at).format("DD/MM/YYYY HH:mm:ss")} />
          </div>

          {Object.keys(extraDetails).length > 0 && (
            <div>
              <h3 style={{ fontSize: "15px", color: "var(--text-color-light, #94a3b8)", marginBottom: "12px", borderBottom: "1px solid var(--modal-border)", paddingBottom: "8px" }}>
                {t.engineeringData}
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                }}
              >
                {Object.entries(extraDetails).map(([key, value]) => (
                  <InfoCard key={key} label={key.replace(/_/g, " ")} value={value?.toString() || t.na} capitalize />
                ))}
              </div>
            </div>
          )}

          {hasHumidityReadings && (
            <div>
              <h3 style={{ fontSize: "15px", color: "var(--text-color-light, #94a3b8)", marginBottom: "16px", borderBottom: "1px solid var(--modal-border)", paddingBottom: "8px" }}>
                {t.humidityTrend || "Humidity Readings Trend"}
              </h3>
              <div style={{ height: "300px", width: "100%", backgroundColor: "var(--header-background, rgba(15, 23, 42, 0.4))", borderRadius: "12px", padding: "20px 20px 5px 0", border: "1px solid var(--modal-border, #334155)" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorHumidity" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="var(--modal-border, #334155)" opacity={0.4} />
                    
                    <XAxis 
                      dataKey="time" 
                      stroke="var(--text-color-light, #94a3b8)" 
                      fontSize={11} 
                      tickLine={false}
                      axisLine={false}
                      minTickGap={30} 
                      tickMargin={10} 
                    />
                    
                    <YAxis 
                      domain={['auto', 'auto']} 
                      stroke="var(--text-color-light, #94a3b8)" 
                      fontSize={11} 
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => `${value.toFixed(0)}%`}
                    />
                    
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: "#0f172a", 
                        borderColor: "#334155", 
                        borderRadius: "8px", 
                        color: "#f8fafc",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                      }}
                      itemStyle={{ color: "#38bdf8", fontWeight: 600 }}
                      formatter={(value) => [`${value}%`, t.humidityTrend ? "Humidity" : "Humidity"]}
                      labelStyle={{ color: "#94a3b8", marginBottom: "4px" }}
                    />
                    
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      name="Humidity" 
                      stroke="#38bdf8" 
                      strokeWidth={2.5} 
                      fillOpacity={1} 
                      fill="url(#colorHumidity)" 
                      dot={false} 
                      activeDot={{ r: 5, fill: "#0f172a", stroke: "#38bdf8", strokeWidth: 2.5 }} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div style={{ fontSize: "12px", color: "var(--text-color-light, #64748b)", textAlign: "center", marginTop: "16px" }}>
            {t.trackingKey} {data.idempotency_key}
          </div>
        </div>
      </div>
    </div>
  );
};